import React from 'react';

import { SpotlightTarget } from '@atlaskit/onboarding';
import WidthDetector from '@atlaskit/width-detector';
import { Flex } from '@atlassian/eoc-flex';
import {
	Calendar,
	CalendarLoading,
	Layer,
	LINE_WIDTH,
	NavigationMode,
	SIDE_HEADER_WIDTH,
	TimelineLoading,
} from '@atlassian/eoc-schedule-common';
import { Skeleton } from '@atlassian/eoc-skeleton';

import { SPOTLIGHT_NAMES } from '../../../../common/constants';
import { DateNavigation } from '../../../../common/ui/date-navigation';
import { useOrderRotationLoading } from '../../../../controllers/edit-store/helpers';
import { useLayers } from '../../../../controllers/list';
import { useSectionUiPreferences } from '../../../../controllers/preferences';
import { useNamesLoading } from '../../../../controllers/schedule';
import { useStore as useScheduleStore } from '../../../../controllers/schedule-store';
import {
	useTimelineEpoch,
	useTimelineLoading,
} from '../../../../controllers/schedule-store/helpers';
import { getNumberOfDays } from '../../../../utils/schedule';
import { isEmpty } from '../../../../utils/validators';

import { RotationList } from './rotation-list';
import { Container, HeaderContainer, LayerName, Scrollable, ScrollableArea } from './styled';
import { WeekList } from './week-list';

const Schedule = () => {
	const [
		{ selectedView, timelineType, timelineData, calendarData, currentDate, schedule },
		{
			setTimelineTypeAction,
			setSelectedViewAction,
			previousPeriodAction,
			nextPeriodAction,
			todayAction,
		},
	] = useScheduleStore();
	const { allLayers, layers } = useLayers({
		withForwarding: !isEmpty(timelineData?.forwardingLayers),
	});
	const [{ startEpoch, endEpoch, nowEpoch, startOffset, endOffset, nowOffset }] =
		useTimelineEpoch();
	const [{ isInitialLoading, isSubsequentLoading }] = useTimelineLoading();
	const [orderRotationLoading] = useOrderRotationLoading();
	const { displayDensity } = useSectionUiPreferences();
	const { isLoading: isResponderNamesLoading } = useNamesLoading();

	const isScheduleLoading = isSubsequentLoading || orderRotationLoading || isResponderNamesLoading;

	// TODO: Add Initial Loading for Calendar
	return (
		<Container>
			{isInitialLoading ? (
				<>
					<HeaderContainer columnGap={1.5} justifyContent="flex-end">
						<Skeleton height={4} width={15} />
						<Skeleton height={4} width={15} />
					</HeaderContainer>
					<Flex flexDirection="column" rowGap={5}>
						<Skeleton height={15} width="100%" />
						<Skeleton height={15} width="100%" />
					</Flex>
				</>
			) : (
				<ScrollableArea>
					<HeaderContainer alignItems="center" justifyContent="flex-end">
						<DateNavigation
							endEpoch={endEpoch}
							isLoading={isScheduleLoading}
							nextPeriod={nextPeriodAction}
							nowEpoch={nowEpoch}
							onTimelineTypeChange={setTimelineTypeAction}
							onToday={todayAction}
							onViewChange={setSelectedViewAction}
							previousPeriod={previousPeriodAction}
							selectedTimelineType={timelineType}
							selectedView={selectedView}
							startEpoch={startEpoch}
							startOffset={startOffset}
							endOffset={endOffset}
							nowOffset={nowOffset}
							scheduleName={schedule.name}
						/>
					</HeaderContainer>

					<SpotlightTarget name={SPOTLIGHT_NAMES.editModeSchedule}>
						<Scrollable>
							{selectedView === NavigationMode.Timeline && (
								<div>
									{allLayers.map((layer) => (
										<React.Fragment key={`fragment-${layer.value}`}>
											<LayerName>{layer.label}</LayerName>
											{isScheduleLoading ? (
												<TimelineLoading
													variant="full"
													size={displayDensity !== 'comfort' ? 'medium' : 'large'}
													numberOfDays={getNumberOfDays({
														timelineType,
														currentDate,
													})}
												/>
											) : (
												<Layer
													nowEpoch={nowEpoch}
													startEpoch={startEpoch}
													endEpoch={endEpoch}
													offset={startOffset}
													size={displayDensity !== 'comfort' ? 'medium' : 'large'}
													variant="full"
													rotationSlot={
														<WidthDetector>
															{(width) => (
																<RotationList
																	width={width ? width - (SIDE_HEADER_WIDTH + LINE_WIDTH) : 0}
																	nowEpoch={nowEpoch}
																	startEpoch={startEpoch}
																	endEpoch={endEpoch}
																	rotations={timelineData?.[layer.value] ?? []}
																	layer={layer}
																/>
															)}
														</WidthDetector>
													}
												/>
											)}
										</React.Fragment>
									))}
								</div>
							)}

							{selectedView === NavigationMode.Calendar && (
								<>
									{isScheduleLoading ? (
										<CalendarLoading />
									) : (
										<Calendar nowEpoch={nowEpoch} startEpoch={startEpoch} offset={startOffset}>
											<WidthDetector>
												{(width) => (
													<WeekList
														width={width ?? 0}
														nowEpoch={nowEpoch}
														weeks={calendarData?.finalWeeks ?? []}
														layer={layers.final}
													/>
												)}
											</WidthDetector>
										</Calendar>
									)}
								</>
							)}
						</Scrollable>
					</SpotlightTarget>
				</ScrollableArea>
			)}
		</Container>
	);
};
export { Schedule };
